import type {
  ConfigurableFormWidgetProps,
  FormFieldConfig,
  FormFieldDisplayOverride,
  FormSubmitValues,
} from './form-widget.types';
import { getIdentificationType } from './identification';

export type SummaryRow = {
  key: string;
  label: string;
  value: string;
  fieldNames: string[];
};

export function getFieldContainerClass(field: FormFieldConfig) {
  const isNameField = ['firstName', 'lastName', 'firstname', 'lastname'].includes(field.name);
  if (isNameField || field.layout?.span === 1) return undefined;
  return '@lg:col-span-2';
}

export function getFieldId(formId: string, fieldName: string) {
  return `${formId}-${fieldName}`;
}

export function hasFilledValue(value: FormSubmitValues[string]) {
  return value !== '' && value !== false && value !== undefined;
}

export function formatFieldValue(field: FormFieldConfig, value: FormSubmitValues[string]) {
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof File !== 'undefined' && value instanceof File) return value.name;

  const textValue = String(value);
  if (field.type === 'select') {
    return field.options?.find((option) => option.value === textValue)?.label ?? textValue;
  }

  return textValue;
}

export function getFirstName(values: FormSubmitValues) {
  if (typeof values.firstName === 'string' && values.firstName) return values.firstName;
  if (typeof values.firstname === 'string' && values.firstname) return values.firstname;
  return undefined;
}

export function createSummaryRows(
  fields: FormFieldConfig[],
  values: FormSubmitValues,
): SummaryRow[] {
  const rows: SummaryRow[] = [];
  const addedNames = new Set<string>();

  fields.forEach((field) => {
    if (field.type === 'checkbox') return;

    if (field.name === 'firstName' || field.name === 'lastName') {
      if (addedNames.has('camelName')) return;
      addedNames.add('camelName');

      const value = getNameValue(values, 'firstName', 'lastName');
      if (!value) return;

      rows.push({
        key: 'fullName',
        label: 'Nombre completo',
        value,
        fieldNames: ['firstName', 'lastName'].filter((name) => hasFilledValue(values[name])),
      });
      return;
    }

    if (field.name === 'firstname' || field.name === 'lastname') {
      if (addedNames.has('lowerName')) return;
      addedNames.add('lowerName');

      const value = getNameValue(values, 'firstname', 'lastname');
      if (!value) return;

      rows.push({
        key: 'fullName',
        label: 'Nombre completo',
        value,
        fieldNames: ['firstname', 'lastname'].filter((name) => hasFilledValue(values[name])),
      });
      return;
    }

    const value = values[field.name];
    if (!hasFilledValue(value)) return;

    rows.push({
      key: field.name,
      label: getSummaryLabel(field, values),
      value: formatFieldValue(field, value),
      fieldNames: [field.name],
    });
  });

  return rows;
}

export function getLoggedInMessage(
  config: ConfigurableFormWidgetProps['config'],
  values: FormSubmitValues,
) {
  const firstName = getFirstName(values);
  const prefix = firstName ? `${firstName}, ` : '';

  if (config.variant === 'course-closed') {
    return `${prefix}te avisaremos cuando este programa vuelva a tener cupos disponibles.`;
  }

  if (config.variant === 'seminar-registration') {
    return `${prefix}usaremos los datos de tu cuenta para completar esta inscripción.`;
  }

  if (config.variant === 'seminar-attendance') {
    return `${prefix}usaremos los datos de tu cuenta para registrar tu asistencia.`;
  }

  if (config.variant === 'resource-download') {
    return `${prefix}usaremos los datos de tu cuenta para enviarte este recurso.`;
  }

  return `${prefix}usaremos los datos de tu cuenta para completar este envío.`;
}

export function getFieldDisplay(
  field: FormFieldConfig,
  values: FormSubmitValues,
): FormFieldDisplayOverride & Pick<FormFieldConfig, 'label' | 'placeholder' | 'helperText'> {
  const override = field.displayWhen
    ? field.displayWhen.values[String(values[field.displayWhen.field] ?? '')]
    : undefined;

  return {
    label: override?.label ?? field.label,
    placeholder: override?.placeholder ?? field.placeholder,
    helperText: override?.helperText ?? field.helperText,
    // El enlace acompaña al texto que lo nombra: una ayuda sobreescrita sin
    // enlace no hereda el del campo base.
    helperLink: override?.helperText ? override.helperLink : field.helperLink,
    inputMode: override?.inputMode ?? getInputMode(field),
  };
}

function getSummaryLabel(field: FormFieldConfig, values: FormSubmitValues) {
  if (field.validation?.type === 'identification') {
    const value = values[field.name];
    return getIdentificationType(
      values[field.validation.countryField],
      typeof value === 'string' ? value : undefined,
    );
  }

  return getFieldDisplay(field, values).label;
}

function getNameValue(values: FormSubmitValues, firstName: string, lastName: string) {
  return [values[firstName], values[lastName]]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ');
}

function getInputMode(field: FormFieldConfig) {
  if (field.inputMode) return field.inputMode;
  if (field.type === 'email') return 'email';
  if (field.type === 'phone') return 'tel';
  return undefined;
}
